import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { EntraService } from '@k12/entra';
import { hasAnyOf, resolveUserAccess } from '../auth/user-access';
import { catchError, filter, from, Observable, of, switchMap, take } from 'rxjs';

/**
 * Factory function that creates a guard to validate user has required app roles.
 *
 * Authorization only: MsalGuard is paired with this guard on the same route and owns
 * authentication. This guard waits for MSAL to finish any in-flight interaction before reading
 * accounts, because emitting a routing decision mid-login cancels the redirect MsalGuard started.
 *
 * Four-state authorization logic:
 * 1. Roles unknown, or the user holds nothing at all → Allow (new user registration flow)
 * 2. User has at least one required app role → Allow (authorized)
 * 3. User is in this portal's registrant group → Allow (registration in flight, role not landed yet)
 * 4. User holds roles or groups but none of the above → Block (cross-app access attempt)
 *
 * @param requiredRoles App role names that grant access to this app
 * @param registrantGroups This portal's Entra groups that mark a registration in progress
 * @returns CanActivateFn that returns Observable that resolves to true or UrlTree for redirect
 */
export function appRoleGuard(requiredRoles: string[], registrantGroups: string[] = []): CanActivateFn {
  if (requiredRoles.length === 0) {
    throw new Error('appRoleGuard: requiredRoles must not be empty');
  }

  return (): Observable<boolean | UrlTree> => {
    const msalService = inject(MsalService);
    const msalBroadcastService = inject(MsalBroadcastService);
    const entraService = inject(EntraService);
    const router = inject(Router);

    const authorize = async (): Promise<boolean | UrlTree> => {
      // Null covers both "MsalGuard is still driving the login" and "the access probe failed".
      // Neither is grounds to deny anyone, and redirecting during a login aborts it.
      const access = await resolveUserAccess(msalService, entraService);

      if (!access) {
        return true;
      }

      // State 1: nothing at all means a registrant who has not been provisioned yet.
      if (access.appRoles.length === 0 && access.groups.length === 0) {
        console.log('[appRoleGuard] State 1: No roles or groups - allowing for registration flow');
        return true;
      }

      // State 2: authorized. Only app roles grant access — a group name is not a role.
      if (hasAnyOf(access.appRoles, requiredRoles)) {
        console.log('[appRoleGuard] State 2: Has required role - allowing access');
        return true;
      }

      // State 3: provisioning puts every new registrant in their portal's generic group, and the app
      // role grant alongside it can silently miss when Entra reports the role without a display name.
      // The group is portal-scoped, so honouring it lets a half-provisioned registrant reach their
      // own wizard without opening any other portal to them.
      if (hasAnyOf(access.groups, registrantGroups)) {
        console.log('[appRoleGuard] State 3: In registrant group - allowing for registration flow');
        return true;
      }

      // State 4: belongs to some other app.
      console.warn('[appRoleGuard] State 4: Wrong roles - blocking access', {
        appRoles: access.appRoles,
        groups: access.groups,
        requiredRoles,
        registrantGroups
      });
      return router.createUrlTree(['/unauthorized']);
    };

    // InteractionStatus.None means MSAL has processed any redirect response and no login is in
    // flight, so the account list is finally trustworthy.
    return msalBroadcastService.inProgress$.pipe(
      filter((status) => status === InteractionStatus.None),
      take(1),
      switchMap(() => from(authorize())),
      catchError((error) => {
        console.error('[appRoleGuard] Authorization check failed:', error);
        // On error, allow access to avoid completely blocking the user
        return of(true);
      })
    );
  };
}
